import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import cx from 'classnames';
import { Classes } from '@blueprintjs/core';
import Drawer from '@hobs/components/Drawer';
import { GhostButton } from '@hobs/components/Buttons';
import Icon from '@hobs/components/Icon';
import Loader from '@hobs/components/Loader';
import Toast from '@hobs/components/Toast';
import WithRoles from '@hobs/hocs/WithRoles';
import { withRouter } from 'react-router-dom';
import { Icon as BpIcon } from '@blueprintjs/core';

import { USER_ROLES } from '@hobs/utils/constants';

import { DiscussionForm } from './DiscussionForm';
import { messageShape } from './propTypes';
import { Message } from './Message';
import './Discussion.scss';

class Discussion extends React.Component {
  constructor(props) {
    super(props);
  }
  messageBottom = React.createRef();

  state = {
    markedUnread: null
  };

  openDiscussion = () => {
    this.setState(
      {
        markedUnread: false
      },
      this.props.onDiscussionOpen
    );
  };

  paramMatchesSrId() {
    return (
      (this.props.match.params.serviceRequestId ===
        this.props.serviceRequestId ||
        this.props.match.params.guid === this.props.serviceRequestId) && // for annex-overview
      this.props.match.params.ticketNumber == this.props.ticketNumber
    ); // for tickets
  }

  isOpen() {
    return (
      this.paramMatchesSrId() && this.props.match.params.action === 'discussion'
    );
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.discussion.discussions.length !==
      prevProps.discussion.discussions.length
    ) {
      this.scrollToBottom();
    }
  }

  componentDidMount() {
    if (this.isOpen() && !this.props.isLoading) {
      this.openDiscussion();
    }
  }

  handleClose = markedUnread => {
    this.props.onDiscussionClose(markedUnread);
  };

  getCurrentDiscussionSrId() {
    return (
      this.props.match.params.assignmentId ||
      this.props.match.params.serviceRequestId ||
      this.props.match.params.guid
    );
  }

  handleSubmit = (values, { resetForm, setSubmitting }) => {
    if (values.message.length <= 0) {
      return;
    }

    this.props
      .onMessageCreate(this.getCurrentDiscussionSrId(), values.message)
      .then(response => {
        if (response.success) {
          resetForm({ message: '' });
        } else {
          Toast.warning(this.props.t('Message not sent'));
        }

        setSubmitting(false);
      });
  };

  handleMarkUnread = async () => {
    const markedUnread = true;
    this.setState({
      markedUnread: markedUnread
    });

    await this.props.onMarkUnread(this.getCurrentDiscussionSrId());
    this.handleClose(markedUnread);
  };

  scrollToBottom = () => {
    this.messageBottom &&
      this.messageBottom.current &&
      this.messageBottom.current.scrollIntoView(true);
  };

  maybeShowEmptyBlock = () => {
    if (this.props.discussion.discussions.length > 0) {
      return null;
    }
    if (this.props.isLoading === true) {
      return null;
    }
    return (
      <div className="discussion--content discussion--content--empty">
        {this.props.t('There are no messages in the discussion.')}
      </div>
    );
  };

  hasNoMessages() {
    return this.props.status === 3;
  }

  hasUnread() {
    return this.props.status === 1 || this.state.markedUnread === true;
  }

  isMarkAsUnreadEnabled() {
    const { discussion, currentUserId } = this.props;
    if (
      currentUserId &&
      discussion.discussions.length > 0 &&
      discussion.discussions[discussion.discussions.length - 1].userId ===
        currentUserId
    ) {
      return false;
    }
    return discussion.markAsUnreadEnabled;
  }

  isOnlyPreviewMode() {
    const { hasUserPermission, discussion } = this.props;
    return (
      hasUserPermission(USER_ROLES.READONLY) ||
      (hasUserPermission(USER_ROLES.HOST_UNIT) && !discussion.canHostUnitEdit)
    );
  }

  shoudlShowLoader() {
    return this.props.isLoading && this.paramMatchesSrId();
  }

  render() {
    const { t, discussion } = this.props;
    const number = discussion.serviceRequestNumber
      ? discussion.serviceRequestNumber
      : '';
    const title = `${t('Discussion with ')} ${
      discussion.discussionWith ? discussion.discussionWith : ''
    } (${number})`;

    if (!discussion) {
      return null;
    }

    return (
      <>
        <GhostButton
          className={cx('discussion--chat-button', {
            'without-initials': !this.props.initials,
            'icon-only': this.props.iconOnly,
            'no-messages': this.hasNoMessages()
          })}
          iconLeft={
            <span className="discussion--chat-button--content">
              <Icon icon="Chat" hasNotification={this.hasUnread()} />
              {this.props.initials && <em>{this.props.initials}</em>}
              {this.props.isSeen && (
                <BpIcon
                  className="discussion--chat-button--seen"
                  icon="tick-circle"
                  iconSize={11}
                />
              )}
            </span>
          }
          type="button"
        />
        {this.shoudlShowLoader() && (
          <div className="discussion-loader-backdrop ">
            <div className="discussion-loader">
              <Loader />
            </div>
          </div>
        )}
        {!this.props.isLoading && (
          <div onClick={e => e.preventDefault()}>
            <Drawer
              isOpen={this.isOpen()}
              onClose={this.handleClose}
              onOpened={this.scrollToBottom}
              size="440px"
              title={title}
              className="discussion"
              icon={<Icon icon="Chat" />}
              isCloseButtonShown={false}
            >
              <div
                onClick={this.handleClose}
                className="discussion--close-drawer"
              >
                <Icon icon="Close" />
              </div>
              <div className={Classes.DRAWER_BODY}>
                <div className={Classes.DIALOG_BODY}>
                  {this.props.isLoading && (
                    <div className="discussion--content discussion--content--empty">
                      <Loader />
                    </div>
                  )}
                  {this.maybeShowEmptyBlock()}
                  {discussion.discussions.length > 0 && (
                    <div className="discussion--content">
                      <div className="discussion--content--messages">
                        {discussion.discussions.map(message => {
                          return (
                            <Message key={message.discussionId} {...message} />
                          );
                        })}
                        <div
                          className="message-bottom"
                          ref={this.messageBottom}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className={Classes.DRAWER_FOOTER}>
                {!this.isOnlyPreviewMode() && (
                  <DiscussionForm
                    t={t}
                    onSubmit={this.handleSubmit}
                    onMarkUnread={this.handleMarkUnread}
                    isLoading={
                      this.props.isLoading || this.props.isLoadingMarkUnread
                    }
                    markAsUnreadEnabled={this.isMarkAsUnreadEnabled()}
                  />
                )}
              </div>
            </Drawer>
          </div>
        )}
      </>
    );
  }
}

Discussion.propTypes = {
  t: PropTypes.func,
  currentUserId: PropTypes.string,
  initials: PropTypes.string,
  onDiscussionOpen: PropTypes.func,
  onDiscussionClose: PropTypes.func,
  onMessageCreate: PropTypes.func,
  onMarkUnread: PropTypes.func,
  hasUserPermission: PropTypes.func,
  discussion: PropTypes.shape({
    total: PropTypes.number,
    discussionWith: PropTypes.string,
    markAsUnreadEnabled: PropTypes.bool,
    discussions: PropTypes.arrayOf(messageShape),
    serviceRequestNumber: PropTypes.string
  }),
  isLoading: PropTypes.bool,
  iconOnly: PropTypes.bool,
  status: PropTypes.oneOf([
    3, // No Messages
    2, // Contains Messages
    1, // New Messages,
    4 // Unknow status - back added and did not informed us
  ]),
  ticketNumber: PropTypes.string,
  isLoadingMarkUnread: PropTypes.bool,
  isSeen: PropTypes.bool
};

export default withRouter(withTranslation()(WithRoles(Discussion)));
